import { useEffect, useRef } from 'react';
import { COLORS } from '../../constants/theme';
import { useAppShellPlatform } from '../../hooks/useAppShellPlatform';
import { formatNumber, formatNetworthPerAcre } from '../../utils/format';
import { isFoodCritical } from '../../utils/alerts';
import { MetricRow } from './MetricRow';
import type { Province } from '../../types/province';

interface ResourceBarSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Not `Province | null`: the pill (the sheet's only trigger) only exists in ResourceBar's
   *  non-null render branch, and `ProvinceContext`'s stale-while-revalidate fetch never resets
   *  `province` back to `null` after a successful first load — verified against
   *  `ProvinceContext.tsx` directly (SF-168 Phase 3 Decision 8b). */
  province: Province;
  mobileTabBarPresent: boolean;
}

/**
 * SF-168 Component 3: bottom-sheet dialog holding everything the mobile pill doesn't —
 * NW/Acre, Gold, Food, Runes, Honour. Mirrors `ConfirmModal.tsx`'s focus-trap/Escape-to-close/
 * focus-return pattern (not reused as a component — `ConfirmModal` hardcodes a centered layout
 * and a Confirm/Cancel footer, the wrong shape for a bottom sheet with no confirm action).
 */
export function ResourceBarSheet({ isOpen, onClose, province, mobileTabBarPresent }: ResourceBarSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const platform = useAppShellPlatform();

  useEffect(() => {
    if (!isOpen) return;

    triggerRef.current = document.activeElement;

    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'button, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusableEls = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableEls.length === 0) return;
        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  // Standard bottom-sheet behavior — prevents iOS rubber-band scroll of the page behind the
  // backdrop. No existing precedent to copy (ConfirmModal doesn't need it, being short/centered).
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // The sheet can only ever be *opened* below lg: (its sole trigger, the pill, is CSS-hidden at
  // lg:+), but nothing stops the viewport crossing to lg:+ while it's already open (SF-129 fixed-
  // overlay hazard — a stray full-screen backdrop on desktop with no visible trigger).
  useEffect(() => {
    if (isOpen && platform === 'desktop') {
      onClose();
    }
  }, [isOpen, platform, onClose]);

  if (!isOpen) return null;

  const { resources, metadata, territory } = province;
  const acres = territory.totals.acres;
  const networth = metadata.networth;

  return (
    <>
      <div
        data-testid="resource-bar-sheet-backdrop"
        className="fixed inset-0 bg-black bg-opacity-70 z-50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        data-testid="resource-bar-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resource-sheet-title"
        className="fixed inset-x-0 z-50 rounded-t-2xl max-h-[75vh] flex flex-col motion-reduce:transition-none transition-transform"
        style={{
          bottom: mobileTabBarPresent ? '96px' : '0px',
          paddingBottom: mobileTabBarPresent ? undefined : 'env(safe-area-inset-bottom)',
          backgroundColor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderBottom: 'none',
        }}
      >
        <div
          className="mx-auto mt-2 mb-1 rounded-full"
          style={{ width: 36, height: 4, backgroundColor: COLORS.border }}
          aria-hidden="true"
        />
        <div className="flex items-center justify-between px-4 pb-2">
          <h4 id="resource-sheet-title" className="text-sm font-semibold" style={{ color: COLORS.text.primary }}>
            Province resources
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-lg leading-none"
            style={{ color: COLORS.text.tertiary }}
          >
            ✕
          </button>
        </div>
        <div className="px-4 pb-4 overflow-y-auto flex flex-col" style={{ gap: 12 }}>
          <p className="text-xs" style={{ color: COLORS.text.tertiary }}>
            Land, Networth &amp; Gold are already shown on the bar above.
          </p>
          <MetricRow label="NW/Acre" value={formatNetworthPerAcre(networth, acres)} unit="gc" weight="tertiary" />
          <MetricRow
            label="Food"
            value={formatNumber(resources.food)}
            unit="bushels"
            weight="secondary"
            valueColor={isFoodCritical(province) ? COLORS.negative : undefined}
          />
          <MetricRow label="Runes" value={formatNumber(resources.runes)} unit="runes" weight="tertiary" />
          <MetricRow label="Honour" value={formatNumber(resources.honour)} unit="" weight="tertiary" />
        </div>
      </div>
    </>
  );
}
